import {useCallback, useEffect, useRef, useState, useSyncExternalStore} from "react";
import {placedOrderSnapshot, subscribePlacedOrders} from "account/placedOrderSettle";
import type {CheckoutDocumentRead} from "pay/checkoutDocument";
import type {CheckoutFailureMessageKey} from "pay/checkoutFailure";
import {useEmbeddedSubscriptionCheckout} from "pay/useEmbeddedSubscriptionCheckout";
import {paymentAdmission} from "pay/withdrawalRight";
import type {PaidMomentActions} from "upsell/usePaidMoment";
import type {OverLimitPlanOffer, OverLimitPricing} from "./overLimitOffer";
import {planRowPrice} from "./overLimitPayDialog";

export interface OverLimitPlanPurchaseInput {
	/** The pay dialog is on screen. Closing it re-seeds the consent unticked and drops any Session. */
	open: boolean;
	/** The quote's plan row, or null where the api offered none; then nothing here can mint. */
	planOffer: OverLimitPlanOffer | null;
	/** The send the first-month price was quoted for, declared on the claim so the api re-decides from the same facts. */
	bytes: number;
	/** Which wall the send met; the claim names it, because the two walls offer different plans. */
	pricing: OverLimitPricing;
	/** The plan row's own funnel surface, held by the host beside the wall's. */
	moment: PaidMomentActions;
}

export interface OverLimitPlanPurchase {
	/** The express consent. Never seeded true; see {@link consent}. */
	acknowledged: boolean;
	/**
	 * The buyer's own act, in both directions. The tick mints the Session, carrying the acknowledgement and the
	 * claim for this send; unticking drops the Session, so a card form never sits under an unticked box.
	 */
	consent: (next: boolean, documentSelection?: CheckoutDocumentRead) => void;
	/** The minted Session's secret, or null while the plan row has no form up. */
	clientSecret: string | null;
	/** Whether the minted Session collects a shipping address, so the form mounts the element for it (api#2390). */
	collectsShippingAddress: boolean;
	/** The mint is in flight. */
	busy: boolean;
	/** The last mint's failure, as the catalogue keys it, or null. */
	error: CheckoutFailureMessageKey | null;
	/**
	 * The api refused the claim: the first-month price no longer applies to this send. Nothing was charged and the
	 * box is unticked again. The dialog sells no one-off to fall back on, on either wall, so the form closes it, asks the
	 * quote again and reopens the sized dialog on the fresh rows (`useCreateForm`).
	 */
	offerUnavailable: boolean;
	/** Our order button placed the order: reported as paid, and the wait for the plan to switch on begins. */
	placed: () => void;
	/** Back, or the close: drop the Session and the consent. */
	dismiss: () => void;
	/** The order is placed and the account has not yet reported the plan; the strip says "confirming". */
	settling: boolean;
}

/**
 * The over-limit pay dialog's plan row (kitecorp/transferify-ui#3229): the first month of the plan that lifts the
 * wall, bought in place on our own order button.
 *
 * A thin adapter by mandate (epic #26). The Session is {@link pay/useEmbeddedSubscriptionCheckout}'s, minted at
 * the exact monthly price the quote named with the claim for this send, so the api re-decides the offer and
 * attaches the first-month coupon itself; every rule about which row exists and what it states lives in
 * `./overLimitPayDialog`, and when the wait for the plan ends lives in `account/placedOrderSettle`.
 *
 * <b>After the order, the composer waits for the plan.</b> The plan switches on when Stripe's `invoice.paid`
 * webhook lands, moments after the order. The order is announced through the shared `completed()` seam, and the
 * app shell's `account/usePlacedOrderAccountRefresh` re-reads the account, bounded, into the signed-in user, as
 * it does for every in-page subscription order (kitecorp/transferify-ui#3679): the composer then sees a
 * subscriber, the wall clears and the send goes. This hook only says the wait for ITS order is running. The
 * bound is what keeps the composer usable: while it waits the strip shows a sentence and no control, and a
 * webhook slower than the bound is found on the next sign-in.
 */
export function useOverLimitPlanPurchase(
	{open, planOffer, bytes, pricing, moment}: OverLimitPlanPurchaseInput,
): OverLimitPlanPurchase {
	const [acknowledged, setAcknowledged] = useState(false);
	// The announcement THIS row's order made, so a wait another surface started is not shown as this one's.
	const [placedOrder, setPlacedOrder] = useState<number | null>(null);
	const announced = useSyncExternalStore(subscribePlacedOrders, placedOrderSnapshot, placedOrderSnapshot);
	const settling = announced.settling && announced.generation === placedOrder;
	const inPage = useEmbeddedSubscriptionCheckout();
	const {startAt, dismiss: dismissSession, completed} = inPage;
	// Read at emit time rather than named as dependencies: the offer and the moment are rebuilt per render of
	// the host.
	const offerRef = useRef(planOffer);
	offerRef.current = planOffer;
	const momentRef = useRef(moment);
	momentRef.current = moment;
	const bytesRef = useRef(bytes);
	bytesRef.current = bytes;
	const pricingRef = useRef(pricing);
	pricingRef.current = pricing;

	const dismiss = useCallback(() => {
		setAcknowledged(false);
		dismissSession();
	}, [dismissSession]);

	// A closed dialog keeps its subtree alive, so a tick and a Session left from the last open would arrive
	// already made on the next: a pre-ticked box is not consent, and an abandoned form is not asked for.
	useEffect(() => {
		if (!open) {
			dismiss();
		}
	}, [open, dismiss]);

	const consent = useCallback((next: boolean, documentSelection?: CheckoutDocumentRead) => {
		setAcknowledged(next);
		if (paymentAdmission(next) !== "mayPay") {
			dismissSession();
			return;
		}
		const offer = offerRef.current;
		if (offer === null) {
			return;
		}
		// The claim names the send and the wall; the api decides the plan and the coupon from them and refuses a
		// claim it would not have offered, so nothing a client sends can name a figure.
		void startAt(offer.priceId, {
			uiMode: "elements",
			immediatePerformanceConsent: true,
			overLimitPlan: {bytes: bytesRef.current, instant: pricingRef.current === "instant"},
			...(documentSelection ? {documentSelection} : {}),
		}).then(outcome => {
			// The first-month price is gone: no retry can bring it back, so the box is unticked rather than left
			// over a form that will never load. A FAILED mint keeps the tick, because the form's Retry repeats
			// the consented request.
			if (outcome === "offerUnavailable") {
				setAcknowledged(false);
			}
		});
	}, [startAt, dismissSession]);

	// The Session mounting is CHECKOUT_OPENED on the plan row's own moment, once per Session.
	const reported = useRef<string | null>(null);
	useEffect(() => {
		const secret = inPage.clientSecret;
		if (secret === null || reported.current === secret) {
			return;
		}
		reported.current = secret;
		momentRef.current.reportCheckoutOpened(planRowPrice(offerRef.current));
	}, [inPage.clientSecret]);

	const placed = useCallback(() => {
		momentRef.current.reportPaid(planRowPrice(offerRef.current));
		// Announces the order; the generation read straight after is the one this row's wait runs under.
		completed();
		setPlacedOrder(placedOrderSnapshot().generation);
		setAcknowledged(false);
	}, [completed]);

	return {
		acknowledged,
		consent,
		clientSecret: inPage.clientSecret,
		collectsShippingAddress: inPage.collectsShippingAddress,
		busy: inPage.busy,
		error: inPage.error,
		offerUnavailable: inPage.offerUnavailable,
		placed,
		dismiss,
		settling,
	};
}
